import { z } from "zod";

const sha = z.string().regex(/^[a-f0-9]{64}$/);
const text = z.string().max(1000);
export const sections = [
  "photos",
  "videos",
  "rooms",
  "portals",
  "pairs",
  "geometry",
  "transitions",
] as const;
export type Section = (typeof sections)[number];
export const artifactSchema = z.object({
  artifact_id: z.string(),
  artifact_schema: z.string(),
  sha256: sha,
  size_bytes: z.number().int().positive(),
  version_id: z.string().min(1),
});
export type Artifact = z.infer<typeof artifactSchema>;
const evidenceSchema = z.object({
  evidence_id: z.string(),
  source: z.enum(["plan", "photo", "geometry", "human", "caption"]),
  state: z.enum(["observed", "inferred", "not_visible", "unknown"]),
  feature: text,
  description: text,
});
export const photoSchema = z.object({
  asset_id: z.string(),
  filename: z.string().max(255),
  asset_sha256: sha,
  status: z.enum([
    "unresolved",
    "provisional",
    "accepted",
    "human_confirmed",
    "conflicted",
  ]),
  plan_room_id: z.string().nullable(),
  camera_room_id: z.string().nullable(),
  visible_room_ids: z.array(z.string()),
  room_group_id: z.string().nullable(),
  reference_asset_ids: z.array(z.string()),
  evidence_ids: z.array(z.string()),
  confidence_kind: z.enum([
    "model_self_report",
    "calibrated",
    "human_assertion",
  ]),
  score: z.number().int().min(0).max(10000),
  calibration_id: z.string().nullable(),
  candidates: z.array(
    z.object({
      room_id: z.string().nullable(),
      score: z.number().int().min(0).max(10000),
      supports: z.array(z.string()),
      contradictions: z.array(z.string()),
      reason: text,
    }),
  ),
  observation: z
    .object({
      status: text,
      view: text,
      evidence: z.array(evidenceSchema),
      artifact: artifactSchema,
      thumbnail: z
        .object({
          url: z
            .string()
            .url()
            .refine((url) => url.startsWith("https://")),
          sha256: sha,
          version_id: z.string(),
        })
        .nullable(),
    })
    .nullable(),
});
export type Photo = z.infer<typeof photoSchema>;
export const videoSchema = z.object({
  asset_id: z.string(),
  status: z.enum(["observed", "partial", "unavailable"]),
  whole_clip_room_assignment: z.literal(false),
  requested_times_us: z.array(z.number().int().nonnegative()).max(12),
  decoded_frames: z.number().int().min(0).max(3600),
  last_decoded_time_us: z.number().int().nonnegative().nullable(),
  diagnostics: z.array(text),
  artifact: artifactSchema,
  samples: z
    .array(
      z.object({
        source_frame_us: z.number().int().nonnegative(),
        decode_index: z.number().int().nonnegative(),
        artifact: artifactSchema,
        observation: z
          .object({
            status: text,
            view: text,
            evidence: z.array(evidenceSchema),
            thumbnail: photoSchema.shape.observation.unwrap().shape.thumbnail,
          })
          .nullable(),
      }),
    )
    .max(12),
});
export type Video = z.infer<typeof videoSchema>;
const pageSchema = z.object({
  schema_version: z.literal("spatial-evidence-page.v1"),
  workload_id: z.string(),
  revision_id: sha,
  parent_revision_id: sha.nullable(),
  generated_revision_id: sha,
  plan_revision_id: sha.nullable(),
  is_correction_draft: z.boolean(),
  correction_sequence: z.number().int().nonnegative(),
  sealed: z.boolean(),
  section: z.enum(sections),
  asset_filter: z.string().nullable().optional(),
  items: z.array(z.record(z.string(), z.unknown())).max(200),
  total: z.number().int().nonnegative(),
  next_cursor: z.string().max(1024).nullable(),
  layout_available: z.boolean(),
  diagnostics: z.array(text),
  pending_asset_ids: z.array(z.string()),
  history_truncated: z.boolean(),
  echo_config: z.object({ config_ref_digest: sha, policy_digest: sha }),
  evidence_config: z.object({
    release_id: z.string(),
    digest: sha,
    schema_version: z.number(),
    reason: z.string(),
    rule_id: z.string().nullable(),
  }),
  artifacts: z.array(artifactSchema).max(2),
});
export type EvidencePage = z.infer<typeof pageSchema>;
export function parsePage(
  value: unknown,
  expected: { workload?: string; section: Section; assetId?: string },
) {
  const page = pageSchema.parse(value);
  if (
    (expected.workload && page.workload_id !== expected.workload) ||
    page.section !== expected.section
  ) {
    throw new Error("Spatial evidence response does not match this Echo");
  }
  if (
    expected.assetId &&
    (page.asset_filter !== expected.assetId ||
      page.items.some((item) => item.asset_id !== expected.assetId))
  ) {
    throw new Error("Spatial evidence photo selection changed");
  }
  if (page.section === "photos")
    page.items.forEach((item) => photoSchema.parse(item));
  if (page.section === "videos")
    page.items.forEach((item) => videoSchema.parse(item));
  return page;
}
export function parseArtifact(
  value: unknown,
  expected: { sha256: string; revision: string },
) {
  const parsed = z
    .object({
      schema_version: z.literal("spatial-artifact-inspector.v1"),
      revision_id: sha,
      artifact: artifactSchema,
      data: z.record(z.string(), z.unknown()),
    })
    .parse(value);
  if (
    parsed.revision_id !== expected.revision ||
    parsed.artifact.sha256 !== expected.sha256 ||
    parsed.data.schema_version !== parsed.artifact.artifact_schema
  )
    throw new Error("Spatial artifact identity changed");
  return parsed;
}
